import { apiClient } from '@/api/apiClient'
import {
  EuaEmissionPriceOverview,
  EuaEmissionPriceOverviewByQuarter,
  OfficialEuaEmissionPrice2026Response,
  Quarter
} from '@/api/models/EuaEmissionPriceOverview'
import { useQuery } from '@tanstack/react-query'

const queryKey = 'eua-emission-price-overview'

export const useEuaEmissionPriceLast30Days = () => {
  const endDate = new Date()
  const startDate = new Date(endDate)
  startDate.setDate(endDate.getDate() - 30)
  return useGetEuaEmissionPriceOverviewRequest({
    queryParams: {
      startDate: startDate.toISOString().slice(0, 10),
      endDate: endDate.toISOString().slice(0, 10)
    }
  })
}

export const useEuaEmissionPriceForFixedTimeRange = (startDate: Date, endDate: Date) => {
  return useGetEuaEmissionPriceOverviewRequest({
    queryParams: {
      startDate: startDate.toISOString().slice(0, 10),
      endDate: endDate.toISOString().slice(0, 10)
    }
  })
}

/**
 * Full EUA price history for CSV export.
 * Pass `enabled: false` (default) and fetch on demand via `refetch()`.
 */
export const useEuaEmissionPriceHistoryForExport = (options?: { enabled?: boolean }) => {
  return useGetEuaEmissionPriceOverviewRequest({
    enabled: options?.enabled ?? false
  })
}

export function buildEuaEmissionPriceOverviewQueryString(queryParams?: {
  startDate?: string
  endDate?: string
}): string {
  const params = new URLSearchParams()

  if (queryParams?.startDate) {
    params.append('startDate', queryParams.startDate)
  }
  if (queryParams?.endDate) {
    params.append('endDate', queryParams.endDate)
  }

  return params.toString() ? `?${params.toString()}` : ''
}

const useGetEuaEmissionPriceOverviewRequest = ({
  queryParams,
  enabled = true
}: {
  queryParams?: {
    startDate?: string
    endDate?: string
  }
  enabled?: boolean
}) => {
  const queryString = buildEuaEmissionPriceOverviewQueryString(queryParams)

  return useQuery({
    queryKey: [queryKey, queryString],
    queryFn: async () => {
      const response = await apiClient.get<EuaEmissionPriceOverview>(
        `/v1/eua-emission-price-overview${queryString}`
      )

      return response.data
    },
    enabled
  })
}

export const useEuaEmissionPriceOverviewByQuarter = (
  year: number,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [queryKey, 'by-quarter', year],
    queryFn: async () => {
      const response = await apiClient.get<EuaEmissionPriceOverviewByQuarter>(
        `/v1/eua-emission-price-overview/by-quarter?year=${year}`
      )
      return response.data
    },
    enabled: options?.enabled ?? true
  })
}

/**
 * Official EUA price for a quarter in 2026.
 * Omit `enabled` to use React Query’s default (query runs when mounted). Pass `enabled: false` to fetch only via `refetch()`.
 */
export const useGetOfficial2026EuaEmissionPrice = (
  quarter: Quarter,
  options?: { enabled?: boolean }
) => {
  return useQuery<OfficialEuaEmissionPrice2026Response, Error>({
    queryKey: [queryKey, 'official', '2026', quarter],
    queryFn: async () => {
      const response = await apiClient.get<OfficialEuaEmissionPrice2026Response>(
        `/v1/eua-emission-price-overview/official/2026?quarter=${quarter}`
      )
      return response.data
    },
    enabled: options?.enabled
  })
}
